<script setup lang="ts">
import { computed, nextTick } from "vue";

import ButtonWithDropdown from "~/components/dumb/ButtonWithDropdown.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import { RecommendationIcon, SettingsIcon, UpgradeRequiredIcon } from "~/icons";
import { makeLinkToSettingsRef } from "~/router/common";
import { ButtonStyle, DropdownMenuItemKind, IconSize, Subscription, UserRole } from "~/shared/enums";
import type { DropdownMenuItem } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore, useUserStore } from "~/stores";

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();

const openChatPanel = () => {
  if (!tenantStore.isSubscriptionGte(Subscription.APP_SUMO)) {
    appStore.setUpgradeConfirmationModalOpen({
      featureAction: "chat with Dart AI",
      subscription: Subscription.PREMIUM,
    });
    return;
  }

  if (appStore.chatPanelOpen) {
    appStore.chatPanel?.createNewChat();
    return;
  }

  appStore.setChatPanelOpen(true);
};

const dropdownSections = computed(() => {
  if (pageStore.isMobile) {
    return [];
  }

  const items: DropdownMenuItem[] = dataStore.skillList().map((skill) => ({
    title: skill.title,
    kind: DropdownMenuItemKind.BUTTON,
    icon: PageIcon,
    iconArgs: {
      page: skill,
      size: IconSize.S,
    },
    onClick: async () => {
      if (!tenantStore.isSubscriptionGte(Subscription.APP_SUMO)) {
        appStore.setUpgradeConfirmationModalOpen({
          featureAction: "use AI skills",
          subscription: Subscription.PREMIUM,
        });
        return;
      }

      openChatPanel();
      await nextTick();
      appStore.chatPanel?.sendContentInNewChat(skill.prompt);
    },
  }));
  items.push({
    title: "Skills settings",
    kind: DropdownMenuItemKind.INTERNAL_LINK,
    hidden: !userStore.isRoleGte(UserRole.MEMBER),
    icon: SettingsIcon,
    navigate: { to: makeLinkToSettingsRef("skills").value },
  });
  return [{ title: "Other skills", items }];
});
</script>

<template>
  <ButtonWithDropdown
    v-if="pageStore.isOnline && userStore.hasAccessToAiChat()"
    :dropdown-sections="dropdownSections"
    :btn-style="ButtonStyle.RECOMMENDATION"
    btn-classes="!pl-1.5 !py-0.5 leading-[18px] !pr-1 group/animate-ai"
    secondary-btn-classes="!px-0.5"
    :icon="tenantStore.isSubscriptionGte(Subscription.APP_SUMO) ? RecommendationIcon : UpgradeRequiredIcon"
    :icon-args="{ class: '-mr-1' }"
    :icon-size="IconSize.XS"
    :text="pageStore.isMobile ? 'AI' : 'AI chat'"
    tooltip-text-secondary="Use AI skills"
    right
    tooltip-text="Chat with Dart AI"
    class="transition-shadow duration-200 app-drag-none hover:shadow-recommendation-glow"
    @click="openChatPanel" />
</template>
